import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/auth-context';
import { 
  UserPreferences, 
  getUserPreferences, 
  updateUserPreference, 
  getLocalPreferences, 
  setLocalPreferences 
} from '../services/userPreferences';

export function useUserPreferences() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Lade Einstellungen beim ersten Aufruf
  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    const loadPreferences = async () => {
      try {
        console.log('[useUserPreferences] Loading preferences for user:', user.id);
        
        // Zuerst lokale Einstellungen laden für sofortige UI
        const localPrefs = getLocalPreferences(user.id);
        setPreferences(localPrefs);
        setLoading(false);

        // Dann Einstellungen aus der Datenbank laden
        const dbPrefs = await getUserPreferences(user.id);
        setPreferences(dbPrefs);
        
        // Lokale Einstellungen mit DB-Daten synchronisieren
        setLocalPreferences(user.id, dbPrefs);
        
        console.log('[useUserPreferences] ✅ Preferences loaded:', dbPrefs);
        setError(null);
        
      } catch (error) {
        console.warn('[useUserPreferences] Failed to load preferences, using local fallback:', error);
        
        // Prüfe ob es ein DB-Schema Problem ist
        if (error instanceof Error && error.message.includes('column "user_preferences" does not exist')) {
          console.warn('[useUserPreferences] ⚠️ user_preferences column does not exist in database');
          setError('user_preferences_column_missing');
        } else {
          setError('preferences_load_error');
        }
        
        const localPrefs = getLocalPreferences(user.id);
        setPreferences(localPrefs);
        setLoading(false);
      }
    };

    loadPreferences();
  }, [user?.id]);

  // Einzelne Einstellung aktualisieren
  const updatePreference = useCallback(async <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => {
    if (!user?.id || !preferences) return false;

    console.log(`[useUserPreferences] Updating preference ${String(key)} to:`, value);

    // Optimistische Aktualisierung
    const newPreferences = { ...preferences, [key]: value };
    setPreferences(newPreferences);
    
    // Lokale Einstellungen sofort aktualisieren
    setLocalPreferences(user.id, newPreferences);

    try {
      // Datenbank aktualisieren
      const success = await updateUserPreference(user.id, key, value);
      if (!success) {
        console.warn('[useUserPreferences] Failed to update preference in database');
        // Fallback: bei DB-Fehler lokale Einstellungen beibehalten
      }
      return success;
    } catch (error) {
      console.error('[useUserPreferences] Error updating preference:', error);
      
      // Bei DB-Schema Fehlern: Nur lokal speichern
      if (error instanceof Error && error.message.includes('column "user_preferences" does not exist')) {
        console.warn('[useUserPreferences] ⚠️ Saving preference only locally due to missing DB column');
        return true; // Erfolgreich lokal gespeichert
      }
      
      return false;
    }
  }, [user?.id, preferences]);

  // Helper-Funktionen für spezifische Einstellungen
  const setShowRepairTasks = useCallback((show: boolean) => {
    return updatePreference('show_repair_tasks', show);
  }, [updatePreference]);

  const setViewMode = useCallback((mode: 'list' | 'kanban') => {
    return updatePreference('view_mode', mode);
  }, [updatePreference]);

  const setStatusFilter = useCallback((filter: string) => {
    return updatePreference('status_filter', filter);
  }, [updatePreference]);

  const setSortBy = useCallback((sortBy: 'due_date' | 'created_at' | 'customer' | 'status') => {
    return updatePreference('sort_by', sortBy);
  }, [updatePreference]);

  const setSortOrder = useCallback((sortOrder: 'asc' | 'desc') => {
    return updatePreference('sort_order', sortOrder);
  }, [updatePreference]);

  const setEnableInventoryManagement = useCallback((enable: boolean) => {
    return updatePreference('enable_inventory_management', enable);
  }, [updatePreference]);

  const setEnableDocumentManagement = useCallback((enable: boolean) => {
    return updatePreference('enable_document_management', enable);
  }, [updatePreference]);

  return {
    preferences,
    loading,
    error,
    updatePreference,
    // Helper-Funktionen
    setShowRepairTasks,
    setViewMode,
    setStatusFilter,
    setSortBy,
    setSortOrder,
    setEnableInventoryManagement,
    setEnableDocumentManagement,
    // Getter für häufig verwendete Werte
    showRepairTasks: preferences?.show_repair_tasks ?? true,
    viewMode: preferences?.view_mode ?? 'kanban',
    statusFilter: preferences?.status_filter ?? 'ALL',
    sortBy: preferences?.sort_by ?? 'due_date',
    sortOrder: preferences?.sort_order ?? 'asc',
    enableInventoryManagement: preferences?.enable_inventory_management ?? false,
    enableDocumentManagement: preferences?.enable_document_management ?? false
  };
} 